import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { attachLectureToClass, listUnassignedLectures } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

// Attach existing lecture to a class library — used from ClassLibrary "Attach existing" button.
export const AttachLectureModal = ({ open, onClose, classroom, onAttached }) => {
  const [rows, setRows] = useState(null);
  const [pickedId, setPickedId] = useState("");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    listUnassignedLectures().then(setRows).catch(() => setRows([]));
    setPickedId(""); setLabel(""); setSaving(false);
  }, [open]);

  const attach = async () => {
    if (!pickedId || saving) return;
    setSaving(true);
    try {
      await attachLectureToClass(classroom.id, pickedId, label.trim());
      toast.success("Attached to class library");
      onAttached();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't attach");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogTitle className="sr-only">Attach existing lecture</DialogTitle>
        <DialogDescription className="sr-only">Attach a lecture from your library into this class.</DialogDescription>
        <div className="p-6 pb-4">
          <h2 className="font-serif text-2xl font-bold">Attach existing lecture</h2>
          <p className="mt-1 text-sm text-muted-foreground">Pick a lecture from your library. It'll be filed into <span className="font-semibold">{classroom.name}</span> and stay hidden from students until you assign it.</p>
        </div>
        <div className="px-6 pb-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Lecture</label>
            {rows === null ? (
              <div className="mt-2 grid place-items-center py-6"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div>
            ) : rows.length === 0 ? (
              <p className="mt-2 rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground text-center">
                Every lecture in your library is already attached to a class. Generate a new one, or detach an existing one first.
              </p>
            ) : (
              <select
                data-testid="attach-lecture-select"
                value={pickedId}
                onChange={(e) => setPickedId(e.target.value)}
                className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60"
              >
                <option value="">— pick a lecture —</option>
                {rows.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.title}{l.book_title ? ` — ${l.book_title}` : ""}{l.chapters ? ` · ${l.chapters}` : ""}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Chapter label <span className="normal-case tracking-normal font-normal text-muted-foreground/60">(optional — defaults to the lecture's chapter)</span></label>
            <input
              data-testid="attach-chapter-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Chapter 6"
              className="mt-2 w-full rounded-xl border border-input bg-background px-4 py-3 outline-none focus:ring-2 focus:ring-ring/60"
            />
          </div>
          <button
            data-testid="attach-submit"
            disabled={!pickedId || saving}
            onClick={attach}
            className="w-full rounded-xl bg-accent text-accent-foreground py-3.5 font-semibold disabled:opacity-60 hover:opacity-90 transition"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mx-auto" /> : "Attach to class"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
